/**
 * errors.js — what to tell staff when Firestore says no.
 *
 * WRITES. Firestore keeps a write it can retry (offline, over the daily quota, server
 * busy) queued on the device: the change shows at once and is sent later, and its
 * promise just waits. A write whose promise REJECTS was refused for good — Firestore
 * has already rolled the change back on screen — so say why:
 *   permission-denied → the database rules said no (signed out, role changed, or the
 *                       rules a new feature needs aren't deployed yet)
 *   not-found         → it was deleted on another device meanwhile
 *   anything else     → couldn't save
 *
 * LISTENERS. A listener that fails is closed for good (hooks/useFeed.js re-opens it);
 * the banner says what happened — over the daily quota (and when it resets), no access,
 * or anything else.
 */

/** i18n key for a refused write. */
export function saveErrorKey(err) {
  switch (err && err.code) {
    case 'permission-denied': return 'common.noPermission';
    case 'not-found': return 'common.goneElsewhere';
    default: return 'common.saveFailed';
  }
}

/**
 * The `.catch` for a write: logs it (always — a refused write is a bug or a rules problem
 * worth seeing in the console) and toasts why. `notify` / `t` = the toast and strings.
 */
export function saveFailedNotifier(notify, t) {
  return err => {
    // eslint-disable-next-line no-console
    console.warn('[daily-ops] save refused:', (err && err.code) || err);
    notify(t(saveErrorKey(err)));
  };
}

/**
 * i18n key + vars for a failed listener. `resetAt` = when the quota resets, as local
 * clock text (logic/dates.js quotaResetTime); without it, Pacific midnight is named.
 */
export function loadErrorKey(err, resetAt = '') {
  switch (err && err.code) {
    case 'resource-exhausted': return { key: 'error.quota', vars: { time: resetAt || '12:00 AM PT' } };
    case 'permission-denied': return { key: 'error.noAccess', vars: {} };
    default: return { key: 'error.load', vars: { msg: (err && (err.message || err.code)) || String(err || '') } };
  }
}
